import useProductLikes from '../../../hooks/global/useProductLikes';
import useTheme from '../../../hooks/ui/useTheme';
import { WithActualPrice } from '../../../providers/MenuProvider';
import { ProductInterface } from '../../../services/exports/Interfaces';
import PriceComparison from '../../menu/PriceComparison';
import HeartIcon from '../../../assets/logo/HeartIcon';
import classNames from 'classnames';
import PlusIcon from '../../../assets/logo/PlusIcon';
import useBasket from '../../../hooks/basket/useBasket';
import { useMemo } from 'react';
import ThumbUpIcon from '../../../assets/logo/ThumbUpIcon';
import useConfig from '../../../hooks/ui/useConfig';
import { useTranslation } from 'react-i18next';

interface Props {
  product: WithActualPrice<ProductInterface>;
  index: number;
  toggleProduct: (product: ProductInterface) => void;
  addedQuantity?: number;
}

const MostPopularProduct = (props: Props) => {
  const { product, toggleProduct, index } = props;
  const theme = useTheme();
  const { toggleProductLike, isLiked } = useProductLikes();
  const liked = isLiked(product.id);
  const originalPrice = product.base_price;
  const { items } = useBasket();
  const { logo_url } = useConfig();

  const addedQuantity = useMemo(
    () => items.reduce((prev, curr) => (curr.product_id === product.id ? prev + curr.quantity : prev), 0),
    [items],
  );

  const config = {
    ...props,
    addedQuantity,
  };

  return (
    <div className="cursor-pointer flex flex-col gap-xs w-full h-full" onClick={() => toggleProduct(product)}>
      <div className="w-full aspect-square rounded-[8.8px] overflow-hidden relative border">
        <img
          src={product.thumbnail_url ?? logo_url}
          alt={product.name}
          className={classNames('w-full h-full object-cover', {
            'w-full h-full !object-contain bg-brand-card': !product.thumbnail_url,
          })}
        />
        <MostPopularProduct.AddButton {...config} />
        <MostPopularProduct.BestSeller index={index} />
      </div>
      <div className="flex flex-col gap-xs h-fit">
        <h6 className={'font-normal text-[15px] text-brand-text-primary line-clamp-2'}>{product.name}</h6>
        <div className="flex items-center gap-[1px]">
          <PriceComparison
            actualPrice={product?.actual_price}
            originalPrice={originalPrice}
            actualPriceStyle={{
              fontWeight: 'semibold',
              color: theme.color.brand.text.primary,
              fontSize: 15,
            }}
            originalPriceStyle={{
              fontWeight: 'semibold',
              fontSize: 15,
            }}
          />
          <div className="flex flex-row items-center">
            <div
              className="ml-xs flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                toggleProductLike(product.id);
              }}
            >
              <HeartIcon
                filled={liked}
                className={classNames('h-3', {
                  'fill-danger': liked,
                  'fill-gray-700': !liked,
                })}
              />
              <span className="text-xs text-brand-text-secondary ml-2xs w-3">{product.likes_count}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MostPopularProduct;

MostPopularProduct.AddButton = ({ addedQuantity }: Props): JSX.Element => {
  const theme = useTheme();
  if (addedQuantity) {
    return (
      <div
        className={classNames(
          'absolute lg:bottom-3 lg:right-3 bottom-2 right-2 bg-brand-card rounded-lg lg:w-9 lg:h-9 w-8 h-8 flex items-center justify-center border',
        )}
      >
        <h6
          className="text-sm text-brand-text-secondary font-medium"
          style={{
            lineHeight: 1,
          }}
        >
          {addedQuantity}
        </h6>
      </div>
    );
  }

  return (
    <div
      className={classNames(
        'absolute lg:bottom-2 lg:right-2 bottom-1 right-1 bg-brand-card rounded-lg lg:w-9 lg:h-9 w-8 h-8 flex items-center justify-center border border-brand-border',
      )}
    >
      <PlusIcon color={theme.color.brand.icon.color} className="!h-[12px] !w-[12px]" />
    </div>
  );
};

MostPopularProduct.BestSeller = ({ index }: { index: number }) => {
  const { t } = useTranslation(null, { keyPrefix: 'Components:Carousel:MostPopularProducts:MostPopularProduct' });
  const theme = useTheme();
  const popularityRank = index + 1;

  if (popularityRank > 3) {
    return null;
  }

  return (
    <span className="flex gap-x-1 font-medium text-xs items-center justify-start absolute top-3 left-3 bg-brand-card rounded-lg lg:px-2 px-1 py-1 shadow-icon dark:shadow-none">
      <ThumbUpIcon color={theme.color.brand.primary} className="lg:h-3 lg:w-3 h-3 w-3" />
      <p className="text-black dark:text-white lg:text-xs text-[10px] text-left">
        {t('most_popular', { rank: popularityRank })}
      </p>
    </span>
  );
};
